import { useCallback, useRef, useState } from "react";
import styles from "./list.module.css";
import { Button, Dialog, Checkbox, TextField, TableRow, TableCell, TableBody, TableHead, Table, Paper, TableContainer, Typography, InputAdornment, IconButton } from "@mui/material";
import { Calendar, ChevronDown, ChevronUp, Edit3, Plus, Search } from "react-feather";
import { Add, ClearRounded, PersonAddAltOutlined } from "@mui/icons-material";
import Loader from "../../../components/Loader";
import { authData } from "../../../components/GetAuth";
import { DateRangePicker } from "rsuite";
import TablePaginationComponent from "../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { useReactToPrint } from "react-to-print";
import AddDriverForm from "../../../components/popups/AddDriverForm";
import AssignDeliveryDriverPopup2 from "../../../components/popups/AssignDeliveryDriverPopup2";
import BookingItemsPanel, { eligibleItemIds } from "./BookingItemsPanel";

const ButtonArea = ({ data }) => {
    const componentRef = useRef();

    const Export = ({ onExport }) => (<Button onClick={(e) => onExport(e.target.value)} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Export To CSV</Button>);

    const convertArrayOfObjectsToCSV = (array) => {
        let result = "";

        const columnDelimiter = ",";
        const lineDelimiter = "\n";
        const keys = ["booking_id", "customer_name", "recepient_name", "delivery_address", "order_status", "contact_no", "container"];

        keys.forEach((key) => {
            if (key === "booking_id") {
                result += "Booking Id";
            } else if (key === "customer_name") {
                result += "Customer Name";
            } else if (key === "recepient_name") {
                result += "Recepient Name";
            } else if (key === "delivery_address") {
                result += "Delivery Address";
            } else if (key === "order_status") {
                result += "Order Status";
            } else if (key === "contact_no") {
                result += "Contact No";
            } else if (key === "container") {
                result += "Container";
            } else {
                result += key;
            }
            result += columnDelimiter;
        });
        result += lineDelimiter;

        array.forEach((item) => {
            let ctr = 0;
            keys.forEach((key) => {
                if (ctr > 0) result += columnDelimiter;
                result += item[key];
                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    };

    const downloadCSV = useCallback(() => {
        let newArray = data.map((row) => {
            return {
                booking_id: row?.booking_id,
                customer_name: row?.customer_name,
                recepient_name: row?.recipient_details?.name,
                delivery_address: row?.recipient_details?.address.replace(/,/g, ""),
                order_status: row?.payment_status === 1 || row?.payment_status === "1" ? "Paid" : "Unpaid",
                contact_no: row?.recipient_details?.phone,
                container: row?.container_name,
            };
        });

        const link = document.createElement("a");
        let csv = convertArrayOfObjectsToCSV(newArray);
        if (csv == null) return;

        const filename = "driver-list.csv";

        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", filename);
        link.click();
    }, [data]);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    return (<div className={styles.ButtonAreaWrap}>
        <Export onExport={() => downloadCSV()} />
        <Button onClick={handlePrint} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Print Bookings</Button>
        <div style={{ display: "none" }}>
            <ComponentToPrintList ref={componentRef} data={data} />
        </div>
    </div>);
}

const ListView = ({ filteredData, setPaymentModalOpen, filterVars, setFilterVars, selected, setSelectBooking, setNoteData, setNoteModalOpen, toggleItem, toggleBookingSelection, setSelected, setSnackbarMessage, setSnackbarOpen, setSnackbarType, refreshBookings, driverList }) => {
    const auth = authData();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [driverFormOpen, setDriverFormOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('delivery:assign-driver') > -1;

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const assignDrivers = () => {
        if (selected.length === 0) {
            setSnackbarType("error");
            setSnackbarOpen(true);
            setSnackbarMessage("Please select at least one item");

            return false;
        }
        setFormOpen(true);
    }

    const onAssign = () => {
        setSnackbarType("success");
        setSnackbarOpen(true);
        setSnackbarMessage("Driver assigned successfully.");

        setSelected([]);
        refreshBookings();
    }

    // Every eligible item across the whole (filtered) list — used to drive
    // the header "select all" checkbox.
    const allEligibleIds = filteredData?.flatMap((b) => eligibleItemIds(b)) || [];
    const allSelected = allEligibleIds.length > 0 && allEligibleIds.every((id) => selected.indexOf(id) > -1);
    const someSelected = allEligibleIds.some((id) => selected.indexOf(id) > -1);

    return (<>
        <Loader open={loading} setOpen={setLoading.bind(this)} />

        <div className={styles.FilterBar}>
            <div className={styles.SearchBox}>
                <Search className={styles.SearchIcon} />
                <TextField
                    fullWidth
                    placeholder="Search booking ID, customer, postcode..."
                    value={filterVars?.searchKey}
                    onChange={(e) => setFilterVars((prev) => { return { ...prev, searchKey: e.target.value }; })}
                    InputProps={{
                        endAdornment: filterVars.searchKey && (<InputAdornment position="end">
                            <IconButton size="small" aria-label="Clear search" onClick={(e) => { setFilterVars((prev) => { return { ...prev, searchKey: '' }; }); }}>
                                <ClearRounded fontSize="small" />
                            </IconButton>
                        </InputAdornment>),
                    }}
                />
            </div>

            <div className={styles.DateFilter}>
                <DateRangePicker
                    format={"dd/MM/yyyy"}
                    placement={"bottomEnd"}
                    placeholder="Search by date"
                    caretAs={Calendar}
                    value={filterVars?.date}
                    showOneCalendar onChange={(e) => setFilterVars((prev) => { return { ...prev, date: e }; })}
                />
            </div>
        </div>

        <div className={`${styles.TableFilterHead}`}>
            <div className={`${styles.FilterCard}`}>
                <div className={`${styles.FilerButton}`}>
                    {canAssign && <Button className={`${styles.TableAddBU}`} onClick={(e) => assignDrivers()}><PersonAddAltOutlined />Assign</Button>}
                    {canAssign && <Button className={`${styles.TableAddBU}`} onClick={(e) => setDriverFormOpen(true)}><Add /> Driver</Button>}
                </div>
            </div>
        </div>

        <div className={`${styles.FilterCardButton}`}>
            {selected.length > 0 ? (<Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1" component="div">{selected.length} item{selected.length > 1 ? "s" : ""} selected</Typography>) : (<ButtonArea style={{ float: "right" }} data={filteredData}></ButtonArea>)}
        </div>

        <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {canAssign && <StyledMainTableCell padding="checkbox">
                                <Checkbox
                                    className={`${styles.AllCheckbox}`}
                                    color="primary"
                                    indeterminate={someSelected && !allSelected}
                                    checked={allSelected}
                                    disabled={allEligibleIds.length === 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelected((prev) => Array.from(new Set([...prev, ...allEligibleIds])));
                                            return;
                                        }
                                        setSelected((prev) => prev.filter((id) => allEligibleIds.indexOf(id) === -1));
                                    }}
                                />
                            </StyledMainTableCell>}
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Customer Name</StyledMainTableCell>
                            <StyledMainTableCell>Recepient Name</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Address</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Notes</StyledMainTableCell>
                            <StyledMainTableCell>Payment Status</StyledMainTableCell>
                            <StyledMainTableCell>Items</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                            const eligibleIds = eligibleItemIds(item);
                            const bookingAllSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.indexOf(id) > -1);
                            const bookingSomeSelected = eligibleIds.some((id) => selected.indexOf(id) > -1);
                            const isExpanded = expandedId === item.id;
                            const totalItems = item?.products?.length || 0;

                            return (<>
                                <StyledMainTableRow
                                    key={item?.id}
                                    className={styles.ResponsiveRow}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    {canAssign && <TableCell padding="checkbox" className={styles.CheckboxCell}>
                                        <Checkbox
                                            color="primary"
                                            indeterminate={bookingSomeSelected && !bookingAllSelected}
                                            checked={bookingAllSelected}
                                            disabled={eligibleIds.length === 0}
                                            onChange={() => toggleBookingSelection(item)}
                                        />
                                    </TableCell>}
                                    <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Customer Name">{item?.customer_name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Recepient Name">{item?.recipient_details?.name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Address">{item?.recipient_details?.address}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Notes">{item?.delivery_note ? <><p>{item?.delivery_note}</p><button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(item?.delivery_note); setSelectBooking(item); }}><Edit3 size={12} /> Update</button></> : <button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(null); setSelectBooking(item); }}><Plus /> Add Note</button>}</TableCell>
                                    <TableCell data-label="Payment Status">
                                        {item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`} onClick={(e) => { setPaymentModalOpen(true); setSelectBooking(item); }}>Unpaid</Button>)}
                                    </TableCell>
                                    <TableCell data-label="Items">
                                        <button className={styles.ExpandToggle} onClick={(e) => setExpandedId(isExpanded ? null : item.id)}>
                                            <span className={styles.ItemCountBadge}>{eligibleIds.length}/{totalItems} ready</span>
                                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                        </button>
                                    </TableCell>
                                </StyledMainTableRow>

                                {isExpanded && <TableRow className={styles.ItemPanelRow}>
                                    <TableCell colSpan={canAssign ? 8 : 7}>
                                        <BookingItemsPanel booking={item} selected={selected} onToggleItem={toggleItem} />
                                    </TableCell>
                                </TableRow>}
                            </>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">No data found</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={filteredData?.length}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>


        <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
            <AssignDeliveryDriverPopup2 setLoading={setLoading.bind(this)} modalClose={setFormOpen.bind(this)} itemIds={selected} onSubmit={onAssign.bind(this)} />
        </Dialog>

        <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
            <AddDriverForm setFormOpen={setDriverFormOpen.bind(this)} setAllData={() => refreshBookings()} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={null} />
        </Dialog>
    </>);
};

export default ListView;